import {HardwareGroupWithContent} from "../../../../api/Hardware";
import {Div10Row} from "../../../../api/QuoteDiv10";
import {Product, ProductReviewStatus} from "../../../../api/Products";
import {BulkEditItem, ProductReplacement} from "../../../../api/QuoteHardware";

export type DynamicMode = "grouped" | "all";

export type ProductRef = {
    id: number;
    name: string;
    productCode: string;
    finish: string;
    manufacturerName: string;
}

// one hardware group item (or div-10 row) that the dialog can change
export type SourceItem = {
    id: number;
    groupName: string;
    product: number;
    baseProduct: number;
    baseCode: string;
    productCode: string;
    name: string;
    finish: string;
    finishName: string;
    manufacturer: number;
    manufacturerName: string;
    manufacturerShortName: string;
    qty: number;
    qtyByCode: {code: string; qty: number}[]; // rendered dynamic codes, empty when not dimensioned
    supplierCategory: number;
    supplierCategoryName: string;
    reviewStatus: ProductReviewStatus | null;
}

export type Target = {
    product: ProductRef | null; // null = keep the same
    supplierCategory: number; // 0 = keep the current supplier
    supplierCategoryName: string;
}

export type ItemEdit = {
    remove: boolean;
    targets: Target[]; // index 0 is the main row, others are split rows
    via?: string; // code of the row that made this change
}

export type Edits = Map<number, ItemEdit>;

export function defaultEdit(): ItemEdit {
    return {
        remove: false,
        targets: [keepTarget()],
    };
}

function keepTarget(): Target {
    return {product: null, supplierCategory: 0, supplierCategoryName: ""};
}

export type ProductItem = {
    id: number;
    key: string;
    splitIndex: number;
    itemIds: number[];

    product: number;
    name: string;
    productCode: string;
    finish: string;
    finishName: string;
    manufacturer: number;
    manufacturerName: string;
    manufacturerShortName: string;
    qty: number;
    reviewStatus: ProductReviewStatus | null;

    bySupplierCategoryOriginal: number; // 0 when the items have different suppliers
    bySupplierCategory: number;
    bySupplierCategoryName: string;

    mixed: boolean;
    remove: boolean;
    replaceWithProduct?: ProductRef;
    splitProduct: boolean;
    changedVia?: string;
}

export function sourceItemsFromHardware(list: HardwareGroupWithContent[]): SourceItem[] {
    const out: SourceItem[] = [];

    list.map(grp => {
        const openingQty = grp.openings.reduce((acc, o) => acc + o.qty, 0);

        grp.hardware.map(h => {
            let qty = 0;
            if(h.overrideHingeActiveQty || h.overrideHingeInActiveQty) {
                const perOpening = coalesce(h.overrideHingeActiveQty, h.activeQty) + coalesce(h.overrideHingeInActiveQty, h.inactiveQty);
                qty = perOpening * openingQty;
            } else if(h.calcTotalQty) {
                qty = h.calcTotalQty;
            } else {
                qty = (h.activeQty + h.commonQty + h.inactiveQty) * openingQty;
            }

            const byCode = new Map<string, number>();
            (h.calcByDimension || []).map(d => {
                byCode.set(d.productCode, (byCode.get(d.productCode) || 0) + d.activeQty + d.inactiveQty + d.commonQty);
                return null;
            });

            out.push({
                id: h.id,
                groupName: grp.name,
                product: h.product,
                baseProduct: h.baseProduct || h.product,
                baseCode: h.baseProductCode || h.productCode,
                productCode: h.productCode,
                name: h.name,
                finish: h.finish,
                finishName: h.finishName,
                manufacturer: h.manufacturer,
                manufacturerName: h.manufacturerName,
                manufacturerShortName: h.manufacturerShortName,
                qty: qty,
                qtyByCode: Array.from(byCode.entries()).map(([code, qty]) => ({code, qty})),
                supplierCategory: h.supplierCategory,
                supplierCategoryName: h.supplierCategoryName,
                reviewStatus: h.reviewStatus || null,
            });

            return null;
        });

        return null;
    });

    return out;
}

export function sourceItemsFromDiv10(list: Div10Row[]): SourceItem[] {
    return list.filter(h => !!h.product).map(h => ({
        id: h.id,
        groupName: "",
        product: h.product,
        baseProduct: h.product,
        baseCode: h.productCode || "", // nullable on div-10 rows
        productCode: h.productCode || "",
        name: h.productName,
        finish: "",
        finishName: "",
        manufacturer: h.manufacturer,
        manufacturerName: h.manufacturerName,
        manufacturerShortName: h.manufacturerShortName,
        qty: h.qty,
        qtyByCode: [],
        supplierCategory: 0,
        supplierCategoryName: "",
        reviewStatus: null,
    }));
}

type Bucket = {
    key: string;
    code: string;
    first: SourceItem;
    items: SourceItem[];
    qty: number;
}

export function buildRows(items: SourceItem[], edits: Edits, mode: DynamicMode, idFor: (key: string) => number): ProductItem[] {
    const buckets = new Map<string, Bucket>();

    const add = (key: string, code: string, item: SourceItem, qty: number) => {
        let b = buckets.get(key);
        if(!b) {
            b = {key, code, first: item, items: [], qty: 0};
            buckets.set(key, b);
        }

        b.items.push(item);
        b.qty += qty;
    }

    items.map(item => {
        if(mode === "grouped") {
            add(`${item.baseProduct}`, item.baseCode, item, item.qty);
        } else if(item.qtyByCode.length === 0) {
            add(`${item.baseProduct}|${item.productCode}`, item.productCode, item, item.qty);
        } else {
            item.qtyByCode.map(c => add(`${item.baseProduct}|${c.code}`, c.code, item, c.qty));
        }

        return null;
    });

    const rows: ProductItem[] = [];

    buckets.forEach(b => {
        const itemEdits = b.items.map(i => edits.get(i.id) || defaultEdit());
        const mixed = new Set(itemEdits.map(editSignature)).size > 1;
        const edit = itemEdits[0];
        const splitCount = Math.max(...itemEdits.map(e => e.targets.length));

        const suppliers = new Set(b.items.map(i => i.supplierCategory));
        const originalSupplier = suppliers.size === 1 ? b.first.supplierCategory : 0;
        const originalSupplierName = suppliers.size === 1 ? b.first.supplierCategoryName : "";

        const via = mode === "all" ? itemEdits
            .filter(e => editSignature(e) !== defaultSignature)
            .map(e => e.via)
            .find(v => !!v && v !== b.code) : undefined;

        for(let i = 0; i < splitCount; i++) {
            const target = edit.targets[i] || keepTarget();
            const supplierChanged = !mixed && target.supplierCategory > 0;

            rows.push({
                id: idFor(`${b.key}#${i}`),
                key: b.key,
                splitIndex: i,
                itemIds: b.items.map(it => it.id),

                product: b.first.baseProduct,
                name: b.first.name,
                productCode: b.code,
                finish: b.first.finish,
                finishName: b.first.finishName,
                manufacturer: b.first.manufacturer,
                manufacturerName: b.first.manufacturerName,
                manufacturerShortName: b.first.manufacturerShortName,
                qty: b.qty,
                reviewStatus: b.first.reviewStatus,

                bySupplierCategoryOriginal: originalSupplier,
                bySupplierCategory: supplierChanged ? target.supplierCategory : originalSupplier,
                bySupplierCategoryName: supplierChanged ? target.supplierCategoryName : originalSupplierName,

                mixed: mixed,
                remove: !mixed && i === 0 && edit.remove,
                replaceWithProduct: mixed ? undefined : (target.product || undefined),
                splitProduct: i > 0,
                changedVia: via,
            });
        }
    });

    // the table groups and orders by manufacturer; this only orders rows inside a group
    rows.sort((a, b) =>
        a.manufacturer - b.manufacturer ||
        a.productCode.localeCompare(b.productCode) ||
        a.splitIndex - b.splitIndex);

    return rows;
}

function editSignature(e: ItemEdit) {
    return JSON.stringify({
        remove: e.remove,
        targets: e.targets.map(t => ({product: t.product?.id || 0, supplierCategory: t.supplierCategory})),
    });
}

const defaultSignature = editSignature(defaultEdit());

export type ApplyResult = {
    edits: Edits;
} | {
    error: string;
    conflictKeys: Set<string>;
}

// applies fx to every item behind the changed rows; rejects the change when it would
// change only some of the items behind another row
export function applyChange(rows: ProductItem[], edits: Edits, items: SourceItem[], changed: ProductItem[], fx: (e: ItemEdit, row: ProductItem) => ItemEdit): ApplyResult {
    const itemIds = new Set(changed.flatMap(r => r.itemIds));
    const changedKeys = new Set(changed.map(r => r.key));

    const conflicts = rows.filter(r =>
        r.splitIndex === 0 &&
        !changedKeys.has(r.key) &&
        r.itemIds.some(id => itemIds.has(id)) &&
        !r.itemIds.every(id => itemIds.has(id)));

    if(conflicts.length > 0) {
        const groupNames = new Map(items.map(i => [i.id, i.groupName]));
        const changedCodes = unique(changed.map(r => r.productCode)).join(", ");

        const parts = conflicts.map(c => {
            const groups = unique(c.itemIds.filter(id => itemIds.has(id)).map(id => groupNames.get(id) || ""));
            return `${c.productCode} in group ${groups.join(", ")} only`;
        });

        return {
            error: `Changing ${changedCodes} also changes ${parts.join("; ")}. Change ${unique(conflicts.map(c => c.productCode)).join(", ")} too, or use Grouped mode.`,
            conflictKeys: new Set(conflicts.map(c => c.key)),
        };
    }

    const next = new Map(edits);
    changed.map(row => {
        row.itemIds.map(id => {
            const current = next.get(id) || defaultEdit();
            const copy: ItemEdit = {
                remove: current.remove,
                targets: current.targets.map(t => Object.assign({}, t)),
            };

            next.set(id, Object.assign(fx(copy, row), {via: row.productCode}));
            return null;
        });

        return null;
    });

    return {edits: next};
}

function ensureTarget(e: ItemEdit, index: number) {
    while(e.targets.length <= index) {
        e.targets.push(keepTarget());
    }

    return e.targets[index];
}

export const editFx = {
    setRemove: (value: boolean) => (e: ItemEdit): ItemEdit => {
        if(value) return Object.assign(defaultEdit(), {remove: true});
        e.remove = false;
        return e;
    },
    setReplace: (product: ProductRef | null) => (e: ItemEdit, row: ProductItem): ItemEdit => {
        e.remove = false;
        ensureTarget(e, row.splitIndex).product = product;
        return e;
    },
    setSupplier: (id: number, name: string) => (e: ItemEdit, row: ProductItem): ItemEdit => {
        const t = ensureTarget(e, row.splitIndex);
        t.supplierCategory = id;
        t.supplierCategoryName = name;
        return e;
    },
    addSplit: () => (e: ItemEdit): ItemEdit => {
        e.remove = false;
        e.targets.push(keepTarget());
        return e;
    },
    removeSplit: () => (e: ItemEdit, row: ProductItem): ItemEdit => {
        e.targets = e.targets.filter((_, i) => i !== row.splitIndex);
        if(e.targets.length === 0) e.targets.push(keepTarget());
        return e;
    },
    reset: () => (e: ItemEdit, row: ProductItem): ItemEdit => {
        e.remove = false;
        e.targets[0] = keepTarget();
        return e;
    },
}

// keeps the drafts of items that still exist, and refreshes the codes and replace targets they show
export function refreshEdits(edits: Edits, oldItems: SourceItem[], items: SourceItem[], product: Product | null): Edits {
    const oldById = new Map(oldItems.map(i => [i.id, i]));
    const byId = new Map(items.map(i => [i.id, i]));
    const next: Edits = new Map();
    edits.forEach((e, id) => {
        const item = byId.get(id);
        if(!item) return;

        next.set(id, Object.assign({}, e, {
            via: refreshVia(e.via, oldById.get(id), item),
            targets: e.targets.map(t => {
                if(!product || t.product?.id !== product.id) return t;
                return Object.assign({}, t, {product: productRef(product)});
            }),
        }));
    });

    return next;
}

// rendered dynamic codes can't be matched to their new code, so they stay as they are
function refreshVia(via: string | undefined, oldItem: SourceItem | undefined, item: SourceItem) {
    if(!via || !oldItem) return via;
    if(via === oldItem.productCode) return item.productCode;
    if(via === oldItem.baseCode) return item.baseCode;
    return via;
}

export function productRef(product: Product): ProductRef {
    return {
        id: product.id,
        name: product.categoryName,
        productCode: product.productCode,
        finish: product.finish,
        manufacturerName: product.manufacturerName,
    };
}

export function buildSubmitItems(items: SourceItem[], edits: Edits): BulkEditItem[] {
    const out: BulkEditItem[] = [];

    items.map(item => {
        const e = edits.get(item.id);
        if(!e) return null;

        if(e.remove) {
            out.push({id: item.id, remove: true});
            return null;
        }

        // "keep the same" is the item's own product; duplicate targets collapse into the first
        const seen = new Set<number>();
        const replaceWith: {product: number; supplierCategory: number}[] = [];
        e.targets.map(t => {
            const product = t.product?.id || item.product;
            if(seen.has(product)) return null;

            seen.add(product);
            replaceWith.push({product, supplierCategory: t.supplierCategory});
            return null;
        });

        if(replaceWith.length === 1 && replaceWith[0].product === item.product) {
            const sc = replaceWith[0].supplierCategory;
            if(sc > 0 && sc !== item.supplierCategory) {
                out.push({id: item.id, supplierCategory: sc});
            }

            return null;
        }

        out.push({id: item.id, replaceWith});
        return null;
    });

    return out;
}

export function buildDiv10Submit(items: SourceItem[], edits: Edits): {replacements: ProductReplacement[], removals: number[]} {
    const replacements: ProductReplacement[] = [];
    const removals: number[] = [];
    const done = new Set<number>();

    items.map(item => {
        const e = edits.get(item.id);
        if(!e || done.has(item.product)) return null;
        done.add(item.product);

        if(e.remove) {
            removals.push(item.product);
            return null;
        }

        unique(e.targets.map(t => t.product?.id || 0))
            .filter(id => id > 0 && id !== item.product)
            .map(id => replacements.push({fromProduct: item.product, toProduct: id}));

        return null;
    });

    return {replacements, removals};
}

function unique<T>(list: T[]): T[] {
    return Array.from(new Set(list));
}

function coalesce(a: any, b: any) {
    if(a === null || a === undefined) return b;
    return a;
}
